import React, { useRef, useEffect, useState } from 'react';
import './SimpleRichTextEditor.css';

interface SimpleRichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

const SimpleRichTextEditor: React.FC<SimpleRichTextEditorProps> = ({ 
  value, 
  onChange, 
  placeholder 
}) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const [selectedHeading, setSelectedHeading] = useState('normal');
  const [selectedAlignment, setSelectedAlignment] = useState('left');

  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value || '';
    }
  }, [value]);

  const handleInput = () => {
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };

  const execCommand = (command: string, value?: string) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
    handleInput();
  };

  const handleFormat = (command: string) => {
    execCommand(command);
  };

  const handleHeading = (level: string) => {
    if (level === 'normal') {
      execCommand('formatBlock', 'p');
    } else {
      execCommand('formatBlock', level);
    }
    setSelectedHeading(level);
  };

  const handleAlignment = (align: string) => {
    if (align === 'left') {
      execCommand('justifyLeft');
    } else if (align === 'center') {
      execCommand('justifyCenter');
    } else if (align === 'right') {
      execCommand('justifyRight');
    } else if (align === 'justify') {
      execCommand('justifyFull');
    }
    setSelectedAlignment(align);
  };

  const handleLink = () => {
    const url = prompt('Enter URL:');
    if (url) {
      execCommand('createLink', url);
    }
  };

  const handleRemoveFormat = () => {
    execCommand('removeFormat');
    setSelectedHeading('normal');
    setSelectedAlignment('left');
  };

  return (
    <div className="simple-rich-editor">
      <div className="editor-toolbar">
        <div className="toolbar-group">
          <select 
            value={selectedHeading} 
            onChange={(e) => handleHeading(e.target.value)}
            className="heading-select"
          >
            <option value="normal">Normal</option>
            <option value="h3">Heading 3</option>
            <option value="h4">Heading 4</option>
            <option value="h5">Heading 5</option>
            <option value="h6">Heading 6</option>
          </select>
        </div>

        <div className="toolbar-group">
          <button
            type="button"
            onClick={() => handleFormat('bold')}
            className="toolbar-btn"
            title="Bold"
          >
            <strong>B</strong>
          </button>
          <button
            type="button"
            onClick={() => handleFormat('italic')}
            className="toolbar-btn"
            title="Italic"
          >
            <em>I</em>
          </button>
          <button
            type="button"
            onClick={() => handleFormat('underline')}
            className="toolbar-btn"
            title="Underline"
          >
            <u>U</u>
          </button>
        </div>

        <div className="toolbar-group">
          <button
            type="button"
            onClick={() => handleAlignment('left')}
            className={`toolbar-btn ${selectedAlignment === 'left' ? 'active' : ''}`}
            title="Align Left"
          >
            ⬅
          </button>
          <button
            type="button"
            onClick={() => handleAlignment('center')}
            className={`toolbar-btn ${selectedAlignment === 'center' ? 'active' : ''}`}
            title="Align Center"
          >
            ⬌
          </button>
          <button
            type="button"
            onClick={() => handleAlignment('right')}
            className={`toolbar-btn ${selectedAlignment === 'right' ? 'active' : ''}`}
            title="Align Right"
          >
            ➡
          </button>
          <button
            type="button"
            onClick={() => handleAlignment('justify')}
            className={`toolbar-btn ${selectedAlignment === 'justify' ? 'active' : ''}`}
            title="Justify"
          >
            ☰
          </button>
        </div>

        <div className="toolbar-group">
          <button
            type="button"
            onClick={handleLink}
            className="toolbar-btn"
            title="Insert Link"
          >
            🔗
          </button>
          <button
            type="button"
            onClick={handleRemoveFormat}
            className="toolbar-btn"
            title="Clear Formatting"
          >
            ✖
          </button>
        </div>
      </div>

      <div
        ref={editorRef}
        className="editor-content"
        contentEditable
        onInput={handleInput}
        data-placeholder={placeholder || 'Enter your content here...'}
        suppressContentEditableWarning={true}
      />
    </div>
  );
};

export default SimpleRichTextEditor;